import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useOutletContext, useParams } from "react-router";
import styled from "styled-components";
import { APP_TITLE } from "../../../config/constant";
import ChartDivergingBars from "../../common/charts/ChartDivergingBars";
import ChartGroupedColumns from "../../common/charts/ChartGroupedColumns";
import FullScreenLoader from "../../common/loaders/FullScreenLoader";
import { CustomSearch } from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import BuyingCycleContext from "./BICommonComponents/BuyingCycleContext";
import BIOverviewCard, { OVERVIEW_CARD_TYPES } from "./BIOverviewComponents/BIOverviewCard";
import SimpleGridContainer from "../../common/forms/SimpleGridContainer";
import BICardWithChart, { CHART_CARD_TYPES, CELL_COLORS } from "./BIOverviewComponents/BICardWithChart";
import { requestBuyingIntelPageData } from "./buyingIntelPageApi";
import { BUYING_INTEL_PAGE_TYPES, DEFAULT_BUYING_CYCLE_ID } from "./buyingIntelPageConfig";
import {
    buildBuyingIntelMetricCardData,
    formatBuyingIntelNumber,
    formatBuyingIntelSignedNumber
} from "./buyingIntelDataUtils";

// #region SCOPED CSS
const StyledPage = styled.div`
    &.biOverviewPage {
        display: flex;
        flex-direction: column;
        margin-top: 16px;
    }

    .category-variance-chart {
        width: 100%;
    }

    .category-variance-chart__note {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-size: 12px;
        line-height: 16px;
    }

    .category-variance-chart__above-plan {
        color: var(--text-and-icons-green-500, #00bb00);
    }

    .category-variance-chart__below-plan {
        color: var(--text-and-icons-red-500, #ff4d4f);
    }
`;
// #endregion

// #region CONSTANTS
const VARIANCE_TONES = Object.freeze({
    danger: {
        chartColor: "#ff4d4f",
        cellColor: CELL_COLORS.DANGER
    },
    success: {
        chartColor: "#00bb00",
        cellColor: CELL_COLORS.COMMITTED
    },
    warning: {
        chartColor: "#ff9900",
        cellColor: CELL_COLORS.WARNING
    }
});
// #endregion

export default function BIOverviewPage() {
    document.title = `${APP_TITLE} - Buying Intelligence Overview`;

    // #region VARS
    const { MainLayoutProps, layoutType } = useOutletContext();
    const { buyingCycleID } = useParams();
    const navigations = useSelector(state => state.navigationAccesses);
    const activeBuyingCycleID = buyingCycleID || DEFAULT_BUYING_CYCLE_ID;
    const currentCompanyID = localStorage.getItem("currentCompanyID");

    const [isLoading, setIsLoading] = useState(true);
    const [pageData, setPageData] = useState(null);

    const contextData = pageData?.buyingCycle || {};
    const metrics = pageData?.metrics || {};
    const commercialOutcome = pageData?.commercialOutcome || {};
    const categoryAllocation = pageData?.categoryAllocation || {};
    const comparableSeasonName = contextData.comparableSeasonName || "Comparable season";

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties
    };
    // #endregion

    // #region COMPUTED PROPS
    const overviewCardData = {
        buyingValue: buildBuyingIntelMetricCardData({
            plan: metrics.buyingValue?.buyPlan,
            committedOrder: metrics.buyingValue?.committedOrder,
            comparableSeason: metrics.buyingValue?.comparableSeasonActual,
            unit: metrics.buyingValue?.currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            changeValue: metrics.buyingValue?.changeVsComparable?.value,
            changePercentage: metrics.buyingValue?.changeVsComparable?.percentage,
            status: metrics.buyingValue?.status
        }),
        units: buildBuyingIntelMetricCardData({
            plan: metrics.units?.buyPlan,
            committedOrder: metrics.units?.committedOrder,
            comparableSeason: metrics.units?.comparableSeasonActual,
            changeValue: metrics.units?.changeVsComparable?.value,
            changePercentage: metrics.units?.changeVsComparable?.percentage,
            status: metrics.units?.status
        }),
        estimatedSellThrough: buildBuyingIntelMetricCardData({
            plan: metrics.estimatedSellThrough?.buyPlan,
            committedOrder: metrics.estimatedSellThrough?.committedOrder,
            comparableSeason: metrics.estimatedSellThrough?.comparableSeasonActual,
            valueSuffix: "%",
            maximumFractionDigits: 1,
            changeMode: "percentagePoints",
            changeValue: metrics.estimatedSellThrough?.changeVsComparable?.value,
            status: metrics.estimatedSellThrough?.status
        }),
        estimatedGrossProfit: buildBuyingIntelMetricCardData({
            plan: metrics.estimatedGrossProfit?.buyPlan,
            committedOrder: metrics.estimatedGrossProfit?.committedOrder,
            comparableSeason: metrics.estimatedGrossProfit?.comparableSeasonActual,
            unit: metrics.estimatedGrossProfit?.currency,
            changeValue: metrics.estimatedGrossProfit?.changeVsComparable?.value,
            changePercentage: metrics.estimatedGrossProfit?.changeVsComparable?.percentage,
            status: metrics.estimatedGrossProfit?.status
        })
    };

    const commercialOutcomeRows = (commercialOutcome.rows || []).map(row => ({
        key: row.metricID,
        label: row.name,
        cells: [
            {
                value: formatBuyingIntelNumber(row.comparableSeasonActual),
                unit: row.currency,
                colorKey: CELL_COLORS.ACTUAL
            },
            {
                value: formatBuyingIntelNumber(row.buyPlan),
                unit: row.currency,
                colorKey: CELL_COLORS.PLAN
            },
            {
                value: formatBuyingIntelNumber(row.committedOrder),
                unit: row.currency,
                colorKey: CELL_COLORS.COMMITTED
            }
        ]
    }));

    const commercialOutcomeChartSeries = [
        {
            name: `${comparableSeasonName} Actual`,
            data: (commercialOutcome.rows || []).map(row => row.comparableSeasonActual)
        },
        {
            name: "Buy Plan",
            data: (commercialOutcome.rows || []).map(row => row.buyPlan)
        },
        {
            name: "Committed order",
            data: (commercialOutcome.rows || []).map(row => row.committedOrder)
        }
    ];

    const categoryAllocationRows = (categoryAllocation.rows || []).map(row => ({
        key: row.categoryID,
        label: row.name,
        cells: [
            {
                value: formatBuyingIntelSignedNumber(row.varianceVsBuyPlan),
                unit: categoryAllocation.varianceCurrency,
                colorKey: VARIANCE_TONES[row.tone]?.cellColor || CELL_COLORS.WARNING
            }
        ]
    }));

    const categoryAllocationChartItems = (categoryAllocation.rows || []).map(row => ({
        label: row.chartName || row.name,
        value: row.varianceVsBuyPlan,
        color: VARIANCE_TONES[row.tone]?.chartColor || VARIANCE_TONES.warning.chartColor
    }));
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        let isMounted = true;
        const handleLoader = loading => {
            if (isMounted) setIsLoading(loading);
        };

        setPageData(null);
        requestBuyingIntelPageData({
            pageType: BUYING_INTEL_PAGE_TYPES.OVERVIEW,
            buyingCycleID: activeBuyingCycleID,
            companyID: currentCompanyID,
            setLoader: handleLoader,
            onError: () => handleLoader(false)
        }).then(data => {
            if (!isMounted || data === undefined) return;
            setPageData(data);
        });

        return () => {
            isMounted = false;
        };
    }, [activeBuyingCycleID, currentCompanyID]);

    useEffect(() => {
        setupHeaders(props, navigations, activeBuyingCycleID);
    }, [props?.layoutType, navigations, activeBuyingCycleID]);
    // #endregion

    return (
        <>
            <FullScreenLoader isLoading={isLoading} />
            {pageData && (
                <StyledPage className="biOverviewPage">
                    <BuyingCycleContext data={contextData} />

                    <SimpleGridContainer type="minWidth" cardMinWidth="395px" gap="24px" autoFit>
                        <BIOverviewCard
                            cardType={OVERVIEW_CARD_TYPES.BUYING_VALUE}
                            data={overviewCardData.buyingValue}
                            comparableSeasonName={comparableSeasonName}
                        />
                        <BIOverviewCard
                            cardType={OVERVIEW_CARD_TYPES.UNITS}
                            data={overviewCardData.units}
                            comparableSeasonName={comparableSeasonName}
                        />
                        <BIOverviewCard
                            cardType={OVERVIEW_CARD_TYPES.ESTIMATED_SELL_THROUGH}
                            data={overviewCardData.estimatedSellThrough}
                            comparableSeasonName={comparableSeasonName}
                        />
                        <BIOverviewCard
                            cardType={OVERVIEW_CARD_TYPES.ESTIMATED_GROSS_PROFIT}
                            data={overviewCardData.estimatedGrossProfit}
                            comparableSeasonName={comparableSeasonName}
                        />
                    </SimpleGridContainer>

                    <SimpleGridContainer type="minWidth" cardMinWidth="820px" gap="24px" style={{ marginTop: "24px" }}>
                        <BICardWithChart
                            cardType={CHART_CARD_TYPES.COMMERCIAL_OUTCOME}
                            tableRows={commercialOutcomeRows}
                            comparableSeasonName={comparableSeasonName}>
                            <ChartGroupedColumns
                                categories={(commercialOutcome.rows || []).map(row => row.name)}
                                series={commercialOutcomeChartSeries}
                                max={4000000}
                                valueFormatter={value =>
                                    `${formatBuyingIntelNumber(value)} ${
                                        commercialOutcome.rows?.[0]?.currency || ""
                                    }`.trim()
                                }
                                ariaLabel="Commercial outcome comparison"
                            />
                        </BICardWithChart>
                        <BICardWithChart
                            cardType={CHART_CARD_TYPES.CATEGORY_ALLOCATION}
                            tableRows={categoryAllocationRows}
                            comparableSeasonName={comparableSeasonName}>
                            <div className="category-variance-chart">
                                <ChartDivergingBars
                                    items={categoryAllocationChartItems}
                                    min={-15000}
                                    max={45000}
                                    valueFormatter={value =>
                                        `${formatBuyingIntelSignedNumber(value)} ${
                                            categoryAllocation.varianceCurrency || ""
                                        }`.trim()
                                    }
                                    ariaLabel="Category allocation variance versus Buy Plan"
                                />
                                <div className="category-variance-chart__note">
                                    <span className="category-variance-chart__above-plan">Right of zero</span>
                                    {" = committed order above the Buy Plan. "}
                                    <span className="category-variance-chart__below-plan">Left of zero</span>
                                    {" = committed order below the Buy Plan."}
                                </div>
                            </div>
                        </BICardWithChart>
                    </SimpleGridContainer>
                </StyledPage>
            )}
        </>
    );
}

// #region SMALL COMPONENTS
function setupHeaders(props, navigations, buyingCycleID) {
    const createMode = navigations?.navigationAccesses?.buyingIntel?.createModeOnly;
    const encodedBuyingCycleID = encodeURIComponent(buyingCycleID);

    props.setControlBarLeftBtns([
        {
            customSearch: <CustomSearch enabled={false} placeholder="Brand, season recommendation or order" />
        }
    ]);
    props.setControlBarCenterBtns([
        { to: "/buying-intel", active: true, disabled: false, title: "BUYING CYCLES" },
        { to: "/buying-intel/portfolio", active: false, title: "PORTFOLIO", disabled: true },
        { to: "/buying-intel/actions", active: false, title: "ACTIONS", disabled: true }
    ]);
    props.setControlBarRightBtns([
        { to: "/buying-intel", active: false, disabled: createMode, title: "View", icon: "eye" },
        {
            to: "/buying-intel/add",
            active: false,
            disabled: false,
            title: "Add",
            icon: "plus"
        },
        {
            to: `/buying-intel/overview/${encodedBuyingCycleID}`,
            active: true,
            disabled: false,
            title: "Overview",
            icon: "presentation-chart-01"
        },
        {
            to: `/buying-intel/buyplan/${encodedBuyingCycleID}`,
            active: false,
            disabled: false,
            title: "Buy plan",
            icon: "lightbulb-05"
        },
        {
            to: `/buying-intel/order/${encodedBuyingCycleID}`,
            active: false,
            disabled: true,
            title: "Order",
            icon: "scale-02"
        },
        {
            to: `/buying-intel/forecast/${encodedBuyingCycleID}`,
            active: false,
            disabled: true,
            title: "Forecast",
            icon: "line-chart-up-01"
        }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: undefined
    });
}
// #endregion
